import {
  ApiClient,
  ApiError,
  type IdentityUserSummary,
  type IdentityUserDetail,
  type IdentityUserUpdate,
} from '../lib/api.js';
import { el, mount, formatDateTime } from '../lib/dom.js';

export interface AccountsViewDeps {
  api: ApiClient;
}

const PAGE_SIZE = 25;

export function renderAccounts(deps: AccountsViewDeps): HTMLElement {
  let offset = 0;
  let query = '';

  const searchInput = el('input', {
    attrs: { type: 'search', placeholder: 'Search username or email…', autocapitalize: 'off', autocorrect: 'off', spellcheck: false },
  }) as HTMLInputElement;
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const summary = el('span', { class: 'who' });
  const list = el('div', { class: 'volume-list' });
  const prevBtn = el('button', { class: 'ghost', text: '‹ Prev', attrs: { disabled: true } });
  const nextBtn = el('button', { class: 'ghost', text: 'Next ›', attrs: { disabled: true } });

  const roleBadge = (role: string) =>
    el('span', { class: role === 'admin' ? 'pill running' : 'pill idle', text: role });

  const renderRow = (u: IdentityUserSummary) => {
    const badges: HTMLElement[] = [roleBadge(u.role)];
    if (u.disabled) badges.push(el('span', { class: 'pill failed', text: 'disabled' }));
    if (u.email && !u.emailVerified) badges.push(el('span', { class: 'pill warning', text: 'email unverified' }));
    if (!u.hasPassword) badges.push(el('span', { class: 'pill idle', text: 'OAuth-only' }));
    return el('div', {
      class: 'volume-row account-row',
      children: [
        el('div', {
          class: 'volume-head',
          children: [
            el('span', { class: 'volume-name', text: u.username }),
            el('span', { class: 'who', children: badges }),
          ],
        }),
        el('div', { class: 'volume-meta', text: `${u.email ?? '(no email)'} · ${u.id}` }),
        el('div', { class: 'volume-actions', children: [
          el('button', { text: 'Manage', on: { click: () => openUserModal(deps.api, u.id, load) } }),
        ] }),
      ],
    });
  };

  const load = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const data = await deps.api.listIdentityUsers({ q: query || undefined, limit: PAGE_SIZE, offset });
      summary.textContent = `${data.total} account${data.total === 1 ? '' : 's'}`;
      mount(list, data.users.length > 0 ? data.users.map(renderRow) : [el('div', { class: 'empty', text: 'No accounts found.' })]);
      prevBtn.toggleAttribute('disabled', offset === 0);
      nextBtn.toggleAttribute('disabled', offset + PAGE_SIZE >= data.total);
    } catch (err) {
      mount(list, [el('div', { class: 'empty', text: err instanceof ApiError ? err.message : 'Failed to load accounts' })]);
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  searchInput.addEventListener('input', () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { query = searchInput.value.trim(); offset = 0; void load(); }, 250);
  });
  refreshBtn.addEventListener('click', () => void load());
  prevBtn.addEventListener('click', () => { offset = Math.max(0, offset - PAGE_SIZE); void load(); });
  nextBtn.addEventListener('click', () => { offset += PAGE_SIZE; void load(); });

  void load();

  return el('div', {
    children: [
      el('div', { class: 'card', children: [
        el('div', { class: 'toolbar', children: [
          el('h2', { text: 'BlueEden accounts', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          summary,
          searchInput,
          refreshBtn,
        ] }),
        el('p', { class: 'help', text: 'Identities of the shared auth provider — common to every BlueEden app.' }),
        list,
        el('div', { class: 'toolbar', children: [prevBtn, el('span', { class: 'spacer' }), nextBtn] }),
      ] }),
    ],
  });
}

// ── User detail + actions modal ───────────────────────────────────

function openUserModal(api: ApiClient, id: string, onChanged: () => void): void {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const body = el('div', { class: 'app-admin-body' });
  const closeBtn = el('button', { class: 'ghost', text: 'Close' });
  const titleEl = el('h3', { text: 'Account' });
  const modal = el('div', {
    class: 'modal modal-wide',
    children: [
      el('header', { children: [
        el('div', { class: 'modal-title', children: [titleEl] }),
        el('div', { class: 'log-actions', children: [closeBtn] }),
      ] }),
      body,
    ],
  });
  backdrop.append(modal);
  document.body.append(backdrop);
  const dismiss = () => { backdrop.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') dismiss(); };
  document.addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) dismiss(); });

  const err = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const ok = el('div', { class: 'alert success', attrs: { hidden: true, role: 'status' } });
  const flash = (node: HTMLElement, msg: string) => {
    node.textContent = msg;
    node.removeAttribute('hidden');
    setTimeout(() => node.setAttribute('hidden', ''), 5000);
  };
  const fail = (e: unknown) => flash(err, e instanceof ApiError ? e.message : 'Action failed');

  const reload = async () => {
    try {
      const { user } = await api.getIdentityUser(id);
      render(user);
      onChanged();
    } catch (e) {
      body.replaceChildren(el('div', { class: 'alert', text: e instanceof ApiError ? e.message : 'Failed to load account' }));
    }
  };

  const kv = (k: string, v: string) => el('div', { class: 'kv', children: [
    el('span', { class: 'kv-key', text: k }),
    el('span', { class: 'kv-value', text: v }),
  ] });

  const render = (u: IdentityUserDetail) => {
    titleEl.textContent = `${u.username}${u.disabled ? ' · DISABLED' : ''}`;

    // Read-only profile.
    const profile = el('div', { class: 'kv-grid', children: [
      kv('ID', u.id),
      kv('Role', u.role),
      kv('Email', u.email ?? '(none)'),
      kv('Email verified', u.emailVerified ? `yes${u.emailVerifiedAt ? ` · ${formatDateTime(u.emailVerifiedAt)}` : ''}` : 'no'),
      kv('Pending email', u.pendingEmail ?? '—'),
      kv('Password', u.hasPassword ? 'set (Argon2id)' : 'none (OAuth-only)'),
      kv('Must change pwd', u.mustChangePassword ? 'yes' : 'no'),
      kv('Locale', u.locale ?? '(auto)'),
      kv('Recent login failures', String(u.recentLoginFailures)),
      kv('Session cutoff', u.sessionCutoff > 0 ? formatDateTime(u.sessionCutoff) : 'none'),
      kv('Created', formatDateTime(u.createdAt)),
      kv('Updated', formatDateTime(u.updatedAt)),
      ...(u.disabled ? [kv('Disabled at', u.disabledAt ? formatDateTime(u.disabledAt) : '—'), kv('Disabled reason', u.disabledReason ?? '—')] : []),
    ] });

    const oauth = u.oauthAccounts.length > 0
      ? el('div', { children: u.oauthAccounts.map((o) => el('div', { class: 'volume-meta', text: `${o.provider}: ${o.email ?? o.displayName ?? '(linked)'} · ${formatDateTime(o.createdAt)}` })) })
      : el('div', { class: 'empty', text: 'No linked providers.' });

    const audit = u.audit.length > 0
      ? el('div', { children: u.audit.map((a) => el('div', { class: 'volume-meta', text: `${formatDateTime(a.createdAt)} · ${a.actor} · ${a.action}${a.details ? ` · ${JSON.stringify(a.details)}` : ''}` })) })
      : el('div', { class: 'empty', text: 'No admin actions recorded.' });

    // ── Editable profile form ──
    const usernameI = el('input', { attrs: { type: 'text', value: u.username } }) as HTMLInputElement;
    const emailI = el('input', { attrs: { type: 'text', value: u.email ?? '' } }) as HTMLInputElement;
    const verifiedI = el('input', { attrs: { type: 'checkbox', ...(u.emailVerified ? { checked: '' } : {}) } }) as HTMLInputElement;
    const roleI = el('select', {}) as HTMLSelectElement;
    for (const r of ['user', 'admin']) roleI.append(el('option', { text: r, attrs: { value: r } }));
    roleI.value = u.role;
    const localeI = el('input', { attrs: { type: 'text', value: u.locale ?? '' } }) as HTMLInputElement;
    const saveBtn = el('button', { class: 'primary', text: 'Save profile' });
    saveBtn.addEventListener('click', async () => {
      const payload: IdentityUserUpdate = {
        username: usernameI.value.trim(),
        email: emailI.value.trim() === '' ? null : emailI.value.trim(),
        emailVerified: verifiedI.checked,
        role: roleI.value,
        locale: localeI.value.trim() === '' ? null : localeI.value.trim(),
      };
      saveBtn.setAttribute('disabled', '');
      try { await api.updateIdentityUser(id, payload); flash(ok, 'Profile saved.'); await reload(); }
      catch (e) { fail(e); saveBtn.removeAttribute('disabled'); }
    });

    const field = (label: string, input: HTMLElement) =>
      el('label', { class: 'form-field', children: [el('span', { class: 'form-label', text: label }), input] });

    const editForm = el('div', { class: 'app-form', children: [
      field('Username', usernameI),
      field('Email', emailI),
      el('label', { class: 'inline-check', children: [verifiedI, el('span', { text: ' Email verified' })] }),
      field('Role', roleI),
      field('Locale', localeI),
      el('div', { class: 'form-actions', children: [saveBtn] }),
    ] });

    // ── Password ──
    const pwdI = el('input', { attrs: { type: 'text', placeholder: 'new password (≥ 8 chars)', autocapitalize: 'off', autocorrect: 'off', spellcheck: false } }) as HTMLInputElement;
    const forceI = el('input', { attrs: { type: 'checkbox', checked: '' } }) as HTMLInputElement;
    const setPwdBtn = el('button', { class: 'primary', text: 'Set password' });
    setPwdBtn.addEventListener('click', async () => {
      if (!pwdI.value) { flash(err, 'Enter a password.'); return; }
      setPwdBtn.setAttribute('disabled', '');
      try { await api.setIdentityPassword(id, pwdI.value, forceI.checked); pwdI.value = ''; flash(ok, 'Password set; sessions revoked.'); await reload(); }
      catch (e) { fail(e); }
      finally { setPwdBtn.removeAttribute('disabled'); }
    });
    const resetBtn = el('button', { text: 'Send reset email', attrs: u.email ? {} : { disabled: '', title: 'No email on file' } });
    resetBtn.addEventListener('click', async () => {
      try { await api.sendIdentityReset(id); flash(ok, 'Reset email sent.'); } catch (e) { fail(e); }
    });

    // ── Sessions / disable / delete ──
    const revokeBtn = el('button', { text: 'Revoke all sessions' });
    revokeBtn.addEventListener('click', async () => {
      try { await api.revokeIdentitySessions(id); flash(ok, 'All sessions revoked.'); await reload(); } catch (e) { fail(e); }
    });
    const toggleBtn = el('button', { class: 'danger', text: u.disabled ? 'Enable account' : 'Disable account' });
    toggleBtn.addEventListener('click', async () => {
      try {
        if (u.disabled) { await api.enableIdentityUser(id); flash(ok, 'Account enabled.'); }
        else {
          const reason = prompt('Reason for disabling (optional):') ?? '';
          await api.disableIdentityUser(id, reason);
          flash(ok, 'Account disabled; sessions revoked.');
        }
        await reload();
      } catch (e) { fail(e); }
    });
    const deleteBtn = el('button', { class: 'danger', text: 'Delete account' });
    deleteBtn.addEventListener('click', async () => {
      if (!confirm(`Permanently delete "${u.username}"?\n\nThis cannot be undone.`)) return;
      if (!confirm('Are you absolutely sure? All identity data for this account will be erased.')) return;
      try { await api.deleteIdentityUser(id); dismiss(); onChanged(); } catch (e) { fail(e); }
    });

    const section = (title: string, ...children: HTMLElement[]) =>
      el('div', { children: [el('div', { class: 'form-section', text: title }), ...children] });

    body.replaceChildren(
      err, ok,
      section('Profile', profile),
      section('Linked OAuth providers', oauth),
      section('Edit', editForm),
      section('Password', el('div', { class: 'app-form', children: [
        field('New password', pwdI),
        el('label', { class: 'inline-check', children: [forceI, el('span', { text: ' Force change at next login' })] }),
        el('div', { class: 'form-actions', children: [setPwdBtn, resetBtn] }),
      ] })),
      section('Sessions & status', el('div', { class: 'form-actions', children: [revokeBtn, toggleBtn, deleteBtn] })),
      section('Recent admin actions', audit),
    );
  };

  void reload();
}
